/** @jsxImportSource react */
import DatahubFaq from '@react/data-hub/components/DatahubFaq';
import Section from '@react/data-hub/components/DatahubSection';
import ExploreCommunities from '@react/data-hub/components/ExploreCommunities';
import HeaderSection from '@react/data-hub/components/HeaderSection';
import PricingLineChart from '@react/data-hub/components/LineChart';
import PricingBarChart from '@react/data-hub/components/PricingBarChart';
import {
  calculateStepSize,
  getFirstAndLastYear,
} from '@react/data-hub/helpers';
import { getMonthlyMedianPrices } from '@react/data-hub/helpers/chartDataHelpers';
import { useDatahub } from '@react/data-hub/hooks/useDatahub';
import withHydration from '@react/services/partialHydration';
import Accordian from '@react/shared/components/Accordian';
import Footer from '@react/shared/components/Footer';
import Header from '@react/shared/components/Header/Header.container';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';

const PricingCalculatorContainer = withHydration(
  () =>
    import(
      /* webpackChunkName: "chunkPricingCalculatorContainer" */ '@react/widgets/pricingCalculator/components/PricingCalculatorContainer'
    )
);

const YearlyTrendsSection = dynamic(
  () =>
    import(
      /* webpackChunkName: "chunkYearlyTrendsSection" */ '@react/data-hub/components/YearlyTrend'
    )
);

const NearbyEntity = dynamic(
  () =>
    import(
      /* webpackChunkName: "chunkNearbyEntity" */ '@react/data-hub/components/NearbyEntity'
    )
);

const fallbackData = {
  '2018': 6160,
  '2019': 5320,
  '2020': 6832,
  '2021': 7077,
  '2022': 6634,
  '2023': 6190,
};

export default function DatahubPage() {
  const router = useRouter();
  const { query } = router;
  const { searchInsights, locationString, locationStringWithCountryCode } =
    useDatahub();

  const { toc, state, city } = query;

  // incase api doesn't return an id in the response jsonapi normalize will return an array
  if (Array.isArray(searchInsights)) {
    return <div>Error</div>;
  }

  // check if normalized is an object
  const { careType, name: cityName, info } = searchInsights || {};
  const { nearbyEntityInfo } = info || {};

  const yearlyTrends = info?.yearlyTrends;

  const isShowingYearlyTrends =
    yearlyTrends &&
    Object.keys(yearlyTrends).length &&
    Object.values(yearlyTrends).every((value) => value > 0);

  const { latestYear } = getFirstAndLastYear(yearlyTrends);
  const title = `Senior Living Pricing Trends | ${locationString} | Seniorly`;
  const description = `Senior living costs will vary based on room type and the level of care needed. Use Seniorly's pricing tools to calculate and better understand senior living costs in your area.`;

  const isPricingCalulatorEnabled = false;

  const trendData = yearlyTrends || fallbackData;

  const monthlyMedianPriceTrends = getMonthlyMedianPrices(trendData);
  const stepSize = calculateStepSize(trendData, 3);

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name='description' content={description} />
      </Head>
      <header className='relative'>
        <Header />
      </header>
      <HeaderSection />

      <div className='mt-4'>
        <div className='mx-auto mb-20 w-full max-w-4xl space-y-8'>
          <Section
            heading={`Senior living pricing ${
              isShowingYearlyTrends ? 'trends and' : ''
            } insights: ${locationString ? locationString : 'National'} ${
              latestYear ? '(' + latestYear + ')' : ''
            }`}
          >
            <YearlyTrendsSection>
              <div className='p-4 md:p-6'>
                <PricingLineChart
                  data={monthlyMedianPriceTrends}
                  stepSize={stepSize}
                />
              </div>
            </YearlyTrendsSection>
          </Section>
          <Section>
            <div className='rounded border border-slate-lighter-90'>
              <div className='bg-slate-lighter-95 p-4'>
                <h4 className='font-medium'>
                  Compare average cost of{' '}
                  <span className='lowercase'>{careType}</span> in{' '}
                  {locationString} with county, state and national averages
                </h4>
              </div>
              <div className='p-4 md:p-6'>
                <PricingBarChart />
              </div>
            </div>
          </Section>

          {nearbyEntityInfo && (
            <Section>
              <h2 className='font-t-l'>
                Compare average cost of{' '}
                <span className='lowercase'>{careType}</span> in{' '}
                {locationString} with nearby cities
              </h2>
              <NearbyEntity nearbyEntity={nearbyEntityInfo} />
            </Section>
          )}

          {isPricingCalulatorEnabled && (
            <Section heading='Senior living cost calculator'>
              <p>
                Use our senior living cost calculator for even more detailed
                pricing. See how community size can affect total costs and
                explore communities in your area.
              </p>

              <Accordian
                className='!pb-0'
                title='Senior living cost calculator'
                variant='A'
                eventProps={{
                  location: 'community pricing section',
                }}
                openByDefault={true}
              >
                <div className='p-m'>
                  <PricingCalculatorContainer
                    initialOptions={{
                      city,
                      state,
                      roomType: 'private',
                      toc,
                      locationString: locationStringWithCountryCode,
                    }}
                    onCtaClick={null}
                  />
                </div>
              </Accordian>
            </Section>
          )}

          <Section heading='Frequently asked questions'>
            <DatahubFaq />
          </Section>
          {!!toc && !!state && !!careType && !!cityName && (
            <ExploreCommunities careType={careType} cityName={cityName} />
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}
